import { Avatar, Box, List, Typography } from "@mui/material";
import Paper from "@mui/material/Paper";
import * as React from "react";
import { useSelector } from "react-redux";
import { isRoutePermitted } from "../../router/others/routerHelpers";
import { routeModules } from "../../router/others/routerModules";
import router from "../../router/router";
import AppMenuLinkContainer from "./links/AppMenuLinkContainer";
import AppMenuLink from "./links/AppMenuNestedLink";
import { deepOrange, deepPurple } from '@mui/material/colors';


export const MenuContext = React.createContext(null);

const concatObjects = (objectToConcatIn, objectToConcat) => {
  const objectToReturn = { ...objectToConcatIn };
  Object.keys(objectToConcat).forEach((key) => {
    objectToReturn[key] = Boolean(objectToReturn[key])
      ? objectToConcat[key].concat(objectToReturn[key])
      : [...objectToConcat[key]];
  });
  return objectToReturn;
};

const AppMenu = ({ open, toggleIsMenuOpen, width, sx }) => {
  const [menuRoutes, setMenuRoutes] = React.useState({});
  const [currentStyle, setCurrentStyle] = React.useState({});
  const { user } = useSelector((state) => state.auth.login);

  const getUserInitials = (name) => {
    return name ? name.split(' ').map((n) => n[0]).join('').toUpperCase() : '?';
  };
  const formatUserName = (lastname, firstname) => {
    const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

    const formattedLastname = lastname ? capitalize(lastname) : '';
    const formattedFirstname = firstname ? capitalize(firstname) : '';

    return `${formattedFirstname} ${formattedLastname}`.trim();
  }

  const stringAvatar = (name) => {
    // If name is null or undefined, return blank initials
    if (!name) {
      return {
        sx: {
          bgcolor: stringToColor(' '),
          marginRight: 2,
          marginLeft: open ? '0' : '-0.5em',
          transition: 'margin 0.3s',
        },
        children: ' ',
      };
    }

    // Split the name and return initials
    const nameParts = name.split(' ');
    const initials = nameParts.map((part) => part && part[0] ? part[0].toUpperCase() : '').join('');

    return {
      sx: {
        bgcolor: stringToColor(name),
        marginRight: 2,
        marginLeft: open ? '0' : '-0.5em',
        transition: 'margin 0.3s',
      },
      children: initials,
    };
  };

  const stringToColor = (string) => {
    let hash = 0;
    let i;

    /* eslint-disable no-bitwise */
    for (i = 0; i < string.length; i += 1) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = '#';

    for (i = 0; i < 3; i += 1) {
      const value = (hash >> (i * 8)) & 0xff;
      color += `00${value.toString(16)}`.slice(-2);
    }
    /* eslint-enable no-bitwise */

    return color;
  }

  const getMenuRoutes = React.useCallback(
    (routes) => {
      let baseRoutes = {};
      routes.forEach(({ children, roles, path, menu }) => {
        if (Boolean(children)) {
          baseRoutes = concatObjects(baseRoutes, getMenuRoutes(children));
        } else if (Boolean(menu) && isRoutePermitted(user.roles, roles)) {
          const moduleKey = Boolean(menu.module) ? menu.module : "noModule";
          baseRoutes[moduleKey] = Boolean(baseRoutes[moduleKey])
            ? [...baseRoutes[moduleKey], { path, menu }]
            : [{ path, menu }];
        }
      });
      return baseRoutes;
    },
    [user]
  );

  React.useEffect(() => {
    setCurrentStyle({
      minWidth: width,
      width: width,
      maxWidth: width,
    });
  }, [width]);

  React.useEffect(() => {
    setMenuRoutes(getMenuRoutes(router.routes));
  }, [getMenuRoutes]);

  return (
    <>
      <MenuContext.Provider value={{ isMenuOpen: open, toggleIsMenuOpen }}>
        <Box
          sx={{
            position: "fixed",
            width: open ? "100%" : "fit-content",
            height: "100%",
            zIndex: 1000,
            backgroundColor: "rgba(0,0,0,.5)",
          }}
          onClick={toggleIsMenuOpen}
        />
        <Paper
          elevation={3}
          sx={{
            ...currentStyle,
            transition: "all ease 500ms",
            overflow: "hidden",
            position: "fixed",
            zIndex: 1001,
            ...sx,
          }}
          square
        >
          <List
            sx={{ width: "100%", padding: 0 }}
            component="nav"
            aria-labelledby="menu-list-subheader"
          >
            {Object.keys(menuRoutes).map((routeModuleKey) => {
              return (
                <AppMenuLinkContainer
                  key={routeModules[routeModuleKey].title}
                  title={routeModules[routeModuleKey].title}
                  icon={React.createElement(routeModules[routeModuleKey].icon)}
                >
                  {menuRoutes[routeModuleKey].map(({ path, menu }) => {
                    return (
                      <AppMenuLink
                        key={path}
                        to={path}
                        icon={React.createElement(menu.icon)}
                      >
                        {menu.title}
                      </AppMenuLink>
                    );
                  })}
                </AppMenuLinkContainer>
              );
            })}
          </List>
          {user && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                width: '100%',
                p: 2,
                backgroundColor: 'background.paper',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                transition: 'all 0.3s',
              }}
            >
              <Avatar {...stringAvatar(`${user.firstname || ''} ${user.lastname || ''}`)} />
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: open ? 'auto' : 0,
                  opacity: open ? 1 : 0,
                  transition: 'opacity 0.3s, width 0.3s',
                }}
              >
                <Typography variant="h6" noWrap>
                  {formatUserName(user.lastname, user.firstname)}
                </Typography>
                {user.description && (
                  <Typography variant="caption" sx={{ color: deepPurple[300], mt: 0.5 }}>
                    {user.description}
                  </Typography>
                )}
                {user.code && (
                  <Typography variant="caption" sx={{ color: deepOrange[300], mt: 0.5 }}>
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box component="span" sx={{ fontWeight: 'fontWeightMedium' }}>
                        Code :&nbsp;
                      </Box>
                      {user.code}
                    </Box>
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Paper>
      </MenuContext.Provider>
    </>
  );
};

export default AppMenu;
